// src/utils/offlineQueue.js
//
// Local IndexedDB queue for work performed while offline. Two stores:
//   - scoreQueue: pending candidate-score save payloads, keyed so a second
//     offline edit to the same field overwrites the first rather than
//     stacking up duplicate entries (mirrors the backend's own upsert
//     behaviour for CandidateScore).
//   - uploadQueue: pending CAD bulk-candidate-work uploads, storing the
//     already-zipped Blobs (IndexedDB, unlike localStorage, can hold Blobs
//     of any size) so a queued upload survives a tab close before it syncs.
import { openDB } from 'idb';

const DB_NAME = 'offline-queue';
const DB_VERSION = 1;
const SCORE_STORE = 'scoreQueue';
const UPLOAD_STORE = 'uploadQueue';

let dbPromise = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(SCORE_STORE)) {
          db.createObjectStore(SCORE_STORE, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(UPLOAD_STORE)) {
          db.createObjectStore(UPLOAD_STORE, { keyPath: 'id' });
        }
      }
    });
  }
  return dbPromise;
}

const scoreKey = (assignmentId, registrationNumber) => `${assignmentId}:${registrationNumber}`;

// `payload` is the exact array already built by buildScorePayload in
// ScoringGuideTable - queuing overwrites any prior queued entry for the
// same candidate under this assignment, so only the latest draft is kept.
export async function queueScoreSave(assignmentId, registrationNumber, payload) {
  const db = await getDb();
  await db.put(SCORE_STORE, {
    key: scoreKey(assignmentId, registrationNumber),
    assignmentId,
    registrationNumber,
    payload,
    queuedAt: Date.now()
  });
}

export async function getQueuedScores() {
  const db = await getDb();
  return db.getAll(SCORE_STORE);
}

export async function removeQueuedScore(key) {
  const db = await getDb();
  await db.delete(SCORE_STORE, key);
}

// `files` is an array of { regNo, blob } for newly-selected files;
// `alreadyUploaded` mirrors the same field sent to the backend endpoint.
export async function queueUpload(assignmentId, { regNos, files, alreadyUploaded }) {
  const db = await getDb();
  const id = `${assignmentId}:${Date.now()}`;
  await db.put(UPLOAD_STORE, {
    id,
    assignmentId,
    regNos,
    files,
    alreadyUploaded,
    status: 'pending', // pending | synced | failed
    queuedAt: Date.now()
  });
  return id;
}

export async function getQueuedUploads() {
  const db = await getDb();
  return db.getAll(UPLOAD_STORE);
}

export async function updateQueuedUpload(id, changes) {
  const db = await getDb();
  const existing = await db.get(UPLOAD_STORE, id);
  if (!existing) return;
  await db.put(UPLOAD_STORE, { ...existing, ...changes });
}

export async function removeQueuedUpload(id) {
  const db = await getDb();
  await db.delete(UPLOAD_STORE, id);
}

export async function getQueueCounts() {
  const db = await getDb();
  const [scores, uploads] = await Promise.all([
    db.count(SCORE_STORE),
    db.count(UPLOAD_STORE)
  ]);
  return { scores, uploads, total: scores + uploads };
}
